import { enums } from "@shared/constants";
import { isAxiosError } from "@shared/utils/errors/error-utils";

/** Delay in milliseconds before retrying a request after a basket lock timeout */
const BASKET_LOCK_RETRY_DELAY_MS = 2000;

/**
 * Checks if the error is a basket lock timeout error from the backend.
 * This occurs when the database lock acquisition times out due to another
 * process holding the lock (e.g., concurrent basket modifications).
 */
export function isBasketLockTimeoutError(error: unknown): boolean {
  if (!isAxiosError(error)) {
    return false;
  }

  const errorCode = error.response?.data?.error_code;

  // Handle both array and single value formats
  if (Array.isArray(errorCode)) {
    return errorCode.includes(
      enums.TICKETS_VALIDATION_ERROR_MAP.TVEM_BASKET_LOCK_TIMEOUT.id
    );
  }

  return (
    errorCode === enums.TICKETS_VALIDATION_ERROR_MAP.TVEM_BASKET_LOCK_TIMEOUT.id
  );
}

/**
 * Returns a promise that resolves after the specified delay
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Retrieves a basket with automatic retry on lock timeout errors.
 *
 * When a basket lock timeout occurs (another process is modifying the basket),
 * this function will wait and retry the request once before giving up.
 *
 * @param fetchFn - The function that performs the actual basket retrieval
 * @returns The result of the fetch function
 * @throws Re-throws the error if it's not a lock timeout or if retry also fails
 */
export async function retrieveBasketWithRetry<T>(
  fetchFn: () => Promise<T>
): Promise<T> {
  try {
    return await fetchFn();
  } catch (error) {
    // If the error is a basket lock timeout, retry once after a delay
    if (isBasketLockTimeoutError(error)) {
      await delay(BASKET_LOCK_RETRY_DELAY_MS);
      return fetchFn();
    }
    // Re-throw other errors
    throw error;
  }
}
